<script setup>
import { ref, readonly, onMounted, provide } from 'vue'

// Shared collapsible tool rail shell. Renders the fixed-width icon rail (85px
// collapsed, 260px expanded) used by Event Search and Selector Search. Owns the
// expand/collapse state (persisted per `storageKey`) and the bottom toggle, and
// provides `railExpanded` to descendant ToolRailItem/Section/Dropdown so they
// render their collapsed/expanded form without prop drilling.
//
// The `id="tool-sidebar"` is intentional and load-bearing: the rail item CSS
// below and ToolRailDropdown's expanded-state overrides both key off it.

const props = defineProps({
  // localStorage key for the collapse state (e.g. 'selectorResults.railExpanded').
  storageKey: {
    type: String,
    required: true
  },
  defaultExpanded: {
    type: Boolean,
    default: false
  },
  ariaLabel: {
    type: String,
    default: 'Tools'
  }
})

const emit = defineEmits(['update:expanded'])

const isExpanded = ref(props.defaultExpanded)

// Descendants read this instead of receiving an expanded prop.
provide('railExpanded', readonly(isExpanded))

onMounted(() => {
  const saved = localStorage.getItem(props.storageKey)
  if (saved !== null) isExpanded.value = saved === 'true'
  // Surface the resolved state so layouts can react (e.g. width bookkeeping).
  emit('update:expanded', isExpanded.value)
})

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
  localStorage.setItem(props.storageKey, String(isExpanded.value))
  emit('update:expanded', isExpanded.value)
}
</script>

<template>
  <div class="rail-wrapper">
    <nav id="tool-sidebar" :class="{ expanded: isExpanded }" :aria-label="ariaLabel">
      <ul class="navbar-nav" ondragstart="return false;">
        <slot />
      </ul>

      <!-- Bottom Section - Toggle -->
      <ul class="navbar-nav nav-bottom">
        <li class="nav-item-wrapper">
          <div @click="toggleExpanded()" class="sidebar-nav toggle-nav">
            <font-awesome-icon class="icon" :icon="['fas', 'table-columns']" />
            <span class="nav-text">{{ isExpanded ? 'collapse' : 'expand' }}</span>
            <div v-if="!isExpanded" class="nav-popout"><span>expand</span></div>
          </div>
        </li>
      </ul>
    </nav>
  </div>
</template>

<!-- Global (id-namespaced) rail chrome. Not scoped: slotted ToolRailItem/Section
     markup lives in the consumer's scope, so these rules must match by selector
     under #tool-sidebar rather than by this component's scope attribute. -->
<style>
.rail-wrapper {
  position: relative;
  flex: none;
  padding: 0;
  padding-left: 0.5rem;
  z-index: 10;
  transition: width 0.3s ease;
}

#tool-sidebar {
  padding: 0;
  background: var(--surface-card);
  display: flex;
  flex-direction: column;
  position: relative;
  width: 85px;
  height: 100%;
  border-radius: var(--radius-xl);
  transition: width 0.3s ease;
}

#tool-sidebar.expanded {
  width: 260px;
}

#tool-sidebar .navbar-nav {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

/* Pin the collapse toggle to the bottom while the options sit at the top. */
#tool-sidebar .nav-bottom {
  margin-top: auto;
}

#tool-sidebar .nav-item-wrapper {
  position: relative;
  padding: 0.25rem 0.75rem;
}

/* Category headers grouping rail items (mirrors the app sidebar .section-header) */
#tool-sidebar .rail-section {
  list-style: none;
}

#tool-sidebar .rail-section-header {
  font-size: 0.66rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-disabled);
  padding: 1rem 1.25rem 0.375rem;
  white-space: nowrap;
}

/* Collapsed-rail replacement for a section header */
#tool-sidebar .rail-separator {
  width: 60%;
  margin: 0.75rem auto;
  border-top: 1px solid var(--border-subtle);
}

#tool-sidebar .sidebar-nav {
  padding: 0.75rem 1rem;
  height: auto;
  min-height: 3rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  transition: var(--transition);
  border-radius: var(--radius-lg);
  position: relative;
  gap: 0;
  cursor: pointer;
  text-decoration: none;
}

#tool-sidebar .sidebar-nav:not(.disabled):hover {
  color: white;
  background-color: var(--surface-hover);
}

/* Selected / active item — neutral raised surface */
#tool-sidebar .sidebar-nav.active,
#tool-sidebar .sidebar-nav.router-link-exact-active {
  background-color: var(--surface-hover);
  color: #fff;
}

#tool-sidebar .sidebar-nav.disabled {
  color: var(--text-disabled);
  cursor: not-allowed;
}

#tool-sidebar .sidebar-nav.disabled:hover {
  background-color: var(--surface-page);
}

#tool-sidebar:not(.expanded) .sidebar-nav {
  justify-content: center;
  padding: 0.75rem 0;
  width: 100%;
}

#tool-sidebar .sidebar-nav .icon {
  font-size: 1.1rem;
  width: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

#tool-sidebar.expanded .sidebar-nav .icon {
  margin-right: 0.75rem;
}

/* Label visibility follows expansion */
#tool-sidebar .nav-text {
  display: none;
}

#tool-sidebar.expanded .nav-text {
  display: inline;
  white-space: nowrap;
  font-size: 0.95rem;
}

/* Hover popouts while collapsed */
#tool-sidebar .nav-popout {
  display: flex;
  position: absolute;
  right: calc(100% - 1.3rem);
  left: auto;
  top: 0;
  height: 100%;
  background: var(--surface-hover);
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  align-items: center;
  white-space: nowrap;
  z-index: 100;
  color: white;
  font-size: 0.95rem;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
  text-decoration: none;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .nav-popout {
  opacity: 1;
  pointer-events: auto;
}

#tool-sidebar:not(.expanded) .nav-item-wrapper:hover .sidebar-nav:not(.disabled) {
  background-color: var(--surface-hover);
  color: white;
}

/* Muted popout for disabled items — page surface + disabled text so a disabled
   item doesn't read as an active/raised chip. Applied at base (not just on hover)
   so the fade-out doesn't flash the enabled colors. */
#tool-sidebar .sidebar-nav.disabled .nav-popout {
  background: var(--surface-page);
  color: var(--text-disabled);
}

@media (prefers-reduced-motion: reduce) {
  #tool-sidebar,
  .rail-wrapper,
  #tool-sidebar .sidebar-nav,
  #tool-sidebar .nav-popout {
    transition: none;
  }
}
</style>
